import React, { useEffect, useState } from 'react';
import axios from 'axios';
import withRoleAccess from '../../hoc/withRoleAccess';
import { useTranslation } from 'react-i18next';
import {
  Layout,
  Tabs,
  Row,
  Col,
  Spin,
  Alert,
  Button,
  Space,
} from 'antd';
import Sidebar from '../../components/Sidebar/Sidebar';
import Header from '../../components/Header/Header';
import SubjectCard from './SubjectCard';
import SubjectDetailsModal from './SubjectDetailsModal';

const { Content } = Layout;

// Education level mapping
const EDUCATION_LEVELS = {
  US_GRADES: Array.from({ length: 13 }, (_, i) => ({
    key: `us-grade-${i + 1}`,
    value: `G. ${i + 1}`,
    label: `G. ${i + 1}`
  })),
  UK_YEARS: Array.from({ length: 12 }, (_, i) => ({
    key: `uk-year-${i + 1}`,
    value: `Y. ${i + 1}`,
    label: `Y. ${i + 1}`
  })),
  GENERAL_LEVELS: [
    { key: 'beginner', value: 'Beginner', label: 'Beginner' },
    { key: 'intermediate', value: 'Intermediate', label: 'Intermediate' },
    { key: 'advanced', value: 'Advanced', label: 'Advanced' },
    { key: 'college', value: 'College', label: 'College' },
    { key: 'general', value: 'General', label: 'General' }
  ]
};

// Create a lookup map for all education levels
const createLevelLookup = () => {
  const lookup = {};
  
  // Add US Grades
  EDUCATION_LEVELS.US_GRADES.forEach(level => {
    lookup[level.value] = `US Grade ${level.value.replace('G. ', '')}`;
  });
  
  // Add UK Years
  EDUCATION_LEVELS.UK_YEARS.forEach(level => {
    lookup[level.value] = `UK Year ${level.value.replace('Y. ', '')}`;
  });
  
  // Add General Levels
  EDUCATION_LEVELS.GENERAL_LEVELS.forEach(level => {
    lookup[level.value] = level.label;
  });
  
  return lookup;
};

const LEVEL_DISPLAY_MAP = createLevelLookup();

function Subject() {
  const { t } = useTranslation();
  const [subjects, setSubjects] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedLevels, setSelectedLevels] = useState({}); // Track selected level for each group
  const API_URL = process.env.REACT_APP_API_URL;

  const handleViewDetailsClick = (subject) => {
    setSelectedSubject(subject);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedSubject(null);
  };

  const handleLevelSelect = (groupName, level) => {
    setSelectedLevels(prev => ({
      ...prev,
      [groupName]: level
    }));
  };

  useEffect(() => {
    const fetchSubjects = async () => {
      try {
        const response = await axios.get(`${API_URL}/subjects`);
        const groupedSubjects = groupBySubjectGroup(response.data);
        setSubjects(groupedSubjects);
        
        // Initialize selected levels - default to "All" for each group
        const initialLevels = {};
        Object.keys(groupedSubjects).forEach(groupName => {
          initialLevels[groupName] = 'All';
        });
        setSelectedLevels(initialLevels);
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching subjects:', error);
        setError(t('errorFetchingSubjects'));
        setLoading(false);
      }
    };

    fetchSubjects();
  }, [API_URL, t]);

  // Group subjects by subject_group_name and also collect levels
  const groupBySubjectGroup = (subjectsArray) => {
    return subjectsArray.reduce((grouped, subject) => {
      const groupName = subject.subject_group_name || 'Other';
      if (!grouped[groupName]) grouped[groupName] = [];
      grouped[groupName].push(subject);
      return grouped;
    }, {});
  };

  // Get unique levels for a subject group and format them
  const getLevelsForGroup = (groupSubjects) => {
    const levels = [...new Set(groupSubjects.map(subject => subject.level).filter(level => level))];
    return levels.sort((a, b) => {
      // Custom sorting to handle different level formats
      const getNumericValue = (level) => {
        if (level.startsWith('G. ')) return parseInt(level.replace('G. ', ''));
        if (level.startsWith('Y. ')) return parseInt(level.replace('Y. ', ''));
        return 999; // Put general levels at the end
      };
      
      const aNum = getNumericValue(a);
      const bNum = getNumericValue(b);
      
      if (aNum === 999 && bNum === 999) {
        // Both are general levels, sort alphabetically
        return a.localeCompare(b);
      }
      
      return aNum - bNum;
    });
  };

  // Format level display name
  const formatLevelDisplay = (level) => {
    return LEVEL_DISPLAY_MAP[level] || level;
  };

  // Filter subjects by selected level
  const filterSubjectsByLevel = (groupSubjects, selectedLevel) => {
    if (selectedLevel === 'All') {
      return groupSubjects;
    }
    return groupSubjects.filter(subject => subject.level === selectedLevel);
  };

  // Sort subject groups alphabetically
  const sortSubjectGroups = (groups) => {
    return groups.sort((a, b) => {
      // Put "Other" at the end
      if (a === 'Other') return 1;
      if (b === 'Other') return -1;
      // Alphabetical sort for everything else
      return a.localeCompare(b);
    });
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 24 }}>
        <Alert message={error} type="error" showIcon />
      </div>
    );
  }

  // Sort subject groups and create tab items
  const sortedGroups = sortSubjectGroups(Object.keys(subjects));
  const items = sortedGroups.map(groupName => {
    const groupSubjects = subjects[groupName];
    const levels = getLevelsForGroup(groupSubjects);
    const selectedLevel = selectedLevels[groupName] || 'All';
    const filteredSubjects = filterSubjectsByLevel(groupSubjects, selectedLevel);

    return {
      key: groupName,
      label: groupName,
      children: (
        <div>
          {/* Level Selection Buttons */}
          {levels.length > 0 && (
            <div style={{ marginBottom: '24px', padding: '16px', background: '#fafafa', borderRadius: '6px' }}>
              <div style={{ marginBottom: '12px', fontWeight: 'bold', color: '#262626' }}>
                {t('selectLevel', 'Select Level')}:
              </div>
              <Space wrap>
                <Button
                  type={selectedLevel === 'All' ? 'primary' : 'default'}
                  onClick={() => handleLevelSelect(groupName, 'All')}
                  size="small"
                >
                  {t('all', 'All')} 
                </Button>
                {levels.map(level => (
                  <Button
                    key={level}
                    type={selectedLevel === level ? 'primary' : 'default'}
                    onClick={() => handleLevelSelect(groupName, level)}
                    size="small"
                  >
                    {formatLevelDisplay(level)} 
                  </Button>
                ))}
              </Space>
            </div>
          )}

          {/* Subject Cards */}
          <Row gutter={[24, 24]} style={{ padding: '24px 0' }}>
            {filteredSubjects.map(subject => (
              <Col xs={24} sm={12} md={8} lg={6} key={subject.subjectid}>
                <SubjectCard
                  subject={subject}
                  onDetailsClick={handleViewDetailsClick}
                />
              </Col>
            ))}
          </Row>

          {/* No subjects message */}
          {filteredSubjects.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px', color: '#8c8c8c' }}>
              {t('noSubjectsFound', 'No subjects found for the selected level')}
            </div>
          )}
        </div>
      ),
    };
  });

  return (
    <Layout>
      <Sidebar />
      <Layout>
        <Header />
        <Content style={{ marginTop: '50px', marginBottom: '70px', padding: '24px', minHeight: '280px', background: '#f5f5f5' }}>
          <div style={{ maxWidth: 1290, margin: '0 auto' }}>
            <Tabs
              items={items}
              type="card"
              size="large"
              style={{
                background: 'white',
                padding: '24px',
                borderRadius: '8px',
                boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
              }}
            />

            <SubjectDetailsModal
              open={showModal}
              subject={selectedSubject}
              onClose={handleCloseModal}
            />
          </div>
        </Content>
      </Layout>
    </Layout>
  );
}

export default withRoleAccess(Subject, ['admin', 'superadmin', 'teacher', 'student']);